import { UserProps } from '@osmosis/shared/types/user';

/**
 * Gets the pendo object from the pendo api.
 * @param apiKey {string} The API key.
 */
const pendoScript = (apiKey: string) => {
  return `(function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
      v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
      o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
      y=e.createElement(n);y.defer=!0;y.src='https://content.product.osmosis.org/agent/static/${apiKey}/pendo.js';
      z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo')`;
};

export default pendoScript;

/**
 * Invokes pendo object method.
 * @param user {UserProps} User properties.
 */
export const pendoInitializer = (user?: UserProps | null) => {
  const isCypressUser =
    user?.email && /^atomation.cypress\+[^@]+@osmosis\.org$/.test(user?.email);

  if (isCypressUser) return;

  const now = new Date();
  const nowUTC = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
  );

  const primeExpires = user?.prime?.expires ?? user?.prime?.primeExpires ?? '';
  const expires = new Date(primeExpires || '1970-01-01');
  const expiresUTC = Date.UTC(
    expires.getUTCFullYear(),
    expires.getUTCMonth(),
    expires.getUTCDate(),
  );

  const diffInMilliseconds = expiresUTC - nowUTC;
  const primeDaysRemaining =
    diffInMilliseconds < 0
      ? -1
      : Math.floor(diffInMilliseconds / (1000 * 60 * 60 * 24)) + 1;

  const pendoPayload = {
    email: user?.email ?? '', // Recommended if using Pendo Feedback, or NPS Email
    full_name: user ? `${user?.preferredName} ${user?.lastName}` : '', // Recommended if using Pendo Feedback
    // uuid: "#{uuid}",
    product: user?.prime?.product ?? '',
    product_id: user?.productID ?? '',
    prime_status_str: user?.prime?.statusString ?? '',
    prime_expires: primeExpires,
    prime_expires_soon: primeDaysRemaining > 0 && primeDaysRemaining < 14,
    prime_expires_within_30days:
      primeDaysRemaining > 0 && primeDaysRemaining < 30,
    hasPaid: !!(user?.prime?.hasPaid ?? user?.hasPaid),
    joined: user?.joinDate ?? '',
    school: user?.profile?.school ?? '',
    schoolID: user?.profile?.schoolID ?? 0,
    type: user?.profile?.type ?? '',
    classYear: user?.profile?.classYear ?? 'None',
    country_code: user?.geoLocation?.country_code ?? '',
    country_name: user?.geoLocation?.country_name ?? '',
    id: user?.userID ?? '',
    stage: user?.profile?.stage ?? '',
    exam_prep: user?.profile?.examPrep ?? '',
    graduation_year: user?.profile?.graduationYear ?? '',
    selected_language: user?.language_name ?? '',
    product_code: user?.productCode ?? '',
  };

  const account =
    (user?.profile?.cohortSchoolID ?? user?.profile?.schoolID)
      ? {
          id: user?.profile?.cohortSchoolID ?? user?.profile?.schoolID ?? 0,
          name: user?.profile?.cohortSchool ?? user?.profile?.school ?? '',
        }
      : undefined;

  globalThis.pendo?.initialize({
    visitor: pendoPayload,
    account,
  });
};
